import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Package, TrendingUp, Users, DollarSign, Loader2 } from 'lucide-react';
import { productsAPI, ordersAPI } from '../../services/api';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [productCount, setProductCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [customerCount, setCustomerCount] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [productsCountRes, ordersRes, customersRes] = await Promise.all([
          productsAPI.getCount(),
          ordersAPI.getAll({ limit: 100 }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/customers/stats/summary`).catch(() => ({ data: {} }))
        ]);
        
        if (isMounted) {
          setProductCount(productsCountRes.data?.count || productsCountRes.count || 0);
          setOrders(ordersRes.data || []);
          setCustomerCount(customersRes.data?.total_customers || 0);
          
          // Calculate revenue from paid orders
          const paidOrders = (ordersRes.data || []).filter(o => o.payment_status === 'paid' || o.status === 'paid');
          const revenue = paidOrders.reduce((sum, order) => sum + (order.total || 0), 0);
          setTotalRevenue(revenue);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching dashboard data:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const stats = [
    {
      title: 'Total Products',
      value: productCount.toString(),
      change: '+12%',
      icon: Package,
      color: 'text-muted-green'
    },
    {
      title: 'Total Orders',
      value: orders.length.toString(),
      change: '+23%',
      icon: TrendingUp,
      color: 'text-soft-terracotta'
    },
    {
      title: 'Total Customers',
      value: customerCount.toString(),
      change: '+8%',
      icon: Users,
      color: 'text-charcoal'
    },
    {
      title: 'Revenue (30d)',
      value: `$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: '+15%',
      icon: DollarSign,
      color: 'text-muted-green'
    }
  ];

  const recentOrders = orders.length > 0 ? orders.slice(0, 5) : [
    { id: 'BXV-100001', customer: 'John Doe', total: 89.99, status: 'Paid', date: '2026-02-07' },
    { id: 'BXV-100002', customer: 'Jane Smith', total: 149.99, status: 'Shipped', date: '2026-02-06' }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2 font-heading">Dashboard</h1>
          <p className="text-[#888888] font-body">Welcome back to BOXVERRA Admin</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-[#141414] rounded-lg p-6 border border-[#D4AF37]/20 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <Icon className="h-8 w-8 text-[#D4AF37]" />
                  <span className="text-sm font-medium text-[#D4AF37] font-body">{stat.change}</span>
                </div>
                <p className="text-3xl font-bold text-white mb-1 font-heading">{stat.value}</p>
                <p className="text-sm text-[#888888] font-body">{stat.title}</p>
              </div>
            );
          })}
        </div>

        {/* Recent Orders */}
        <div className="bg-[#141414] rounded-lg p-6 border border-[#D4AF37]/20 shadow-lg">
          <h2 className="text-2xl font-bold text-white mb-6 font-heading">Recent Orders</h2>
          <div className="space-y-3">
            {recentOrders.map((order, index) => (
              <div key={order._id || order.id || index} className="flex items-center justify-between p-4 border border-[#D4AF37]/20 rounded hover:border-[#D4AF37]/50 transition-colors bg-[#0A0A0A]">
                <div>
                  <p className="font-semibold text-white font-body">{order.order_number || order.id}</p>
                  <p className="text-sm text-[#888888] font-body">{order.shipping_address?.first_name || order.customer || 'Customer'}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#D4AF37] font-body">${order.total?.toFixed(2) || order.total}</p>
                  <span className="text-sm px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] font-body">
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;